import { useCallback, useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";

interface PendingInvitation {
  organisationUuid: string;
  organisationName: string;
  organisationSlug: string;
  tier: string;
  role: "Admin" | "Contributor" | "Auditor_Viewer";
  invitedAt: string | null;
  invitedBy: { fullName: string; email: string } | null;
}

const ROLE_LABEL: Record<PendingInvitation["role"], string> = {
  Admin: "Admin",
  Contributor: "Contributor",
  Auditor_Viewer: "Auditor (read-only)",
};

export function PendingInvitationsBanner() {
  const { refreshSession } = useAuth();
  const [invites, setInvites] = useState<PendingInvitation[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const res = await api<{ invitations: PendingInvitation[] }>("/me/invitations");
      setInvites(res.invitations);
    } catch (err) {
      setError(formatApiError(err)[0] ?? "Failed to load invitations");
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  if (error) {
    return null;
  }
  if (!invites || invites.length === 0) {
    return null;
  }

  const accept = async (orgUuid: string) => {
    setBusy(true);
    try {
      await api(`/me/invitations/${orgUuid}/accept`, { method: "POST" });
      await refresh();
      await refreshSession();
    } finally {
      setBusy(false);
    }
  };

  const decline = async (orgUuid: string) => {
    setBusy(true);
    try {
      await api(`/me/invitations/${orgUuid}/decline`, { method: "POST" });
      await refresh();
    } finally {
      setBusy(false);
    }
  };

  return (
    <section
      role="region"
      aria-label="Pending invitations"
      className="rounded-lg border border-brand-200 bg-brand-50 p-4 dark:border-brand-900/40 dark:bg-brand-500/10"
    >
      <h2 className="text-sm font-semibold text-brand-900 dark:text-brand-100">
        You have {invites.length} pending invitation{invites.length === 1 ? "" : "s"}
      </h2>
      <ul className="mt-3 space-y-2">
        {invites.map((inv) => (
          <li
            key={inv.organisationUuid}
            className="flex items-center justify-between gap-3 rounded-md border border-brand-200 bg-white p-3 text-sm dark:border-brand-900/40 dark:bg-slate-800"
          >
            <div>
              <p className="font-medium text-slate-900 dark:text-slate-100">
                {inv.organisationName}
              </p>
              <p className="text-xs text-slate-600 dark:text-slate-400">
                Role: {ROLE_LABEL[inv.role]}
                {inv.invitedBy ? ` · invited by ${inv.invitedBy.fullName}` : ""}
              </p>
            </div>
            <div className="flex shrink-0 gap-2">
              <button
                type="button"
                onClick={() => void accept(inv.organisationUuid)}
                disabled={busy}
                className="rounded-md bg-brand-600 px-3 py-1 text-xs font-semibold text-white hover:bg-brand-700 disabled:opacity-60"
              >
                Accept
              </button>
              <button
                type="button"
                onClick={() => void decline(inv.organisationUuid)}
                disabled={busy}
                className="rounded-md border border-slate-300 bg-white px-3 py-1 text-xs font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-60 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-200"
              >
                Decline
              </button>
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
}
